import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth token storage key
const STORAGE_KEY = "wrvu_auth";

// Helper to get stored token
function getToken() {
  const stored = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const data = JSON.parse(stored);
      return data.token;
    } catch {
      return null;
    }
  }
  return null;
}

// Create axios instance with auth interceptor
const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API calls (no token needed)
export async function loginUser(email, password) {
  const res = await axios.post(`${API}/auth/login`, { email, password });
  return res.data;
}

export async function registerUser(email, password, securityQuestion = null, securityAnswer = null) {
  const payload = { email, password };
  if (securityQuestion && securityAnswer) {
    payload.security_question = securityQuestion;
    payload.security_answer = securityAnswer;
  }
  const res = await axios.post(`${API}/auth/register`, payload);
  return res.data;
}

// Protected API calls (token attached automatically)
export async function fetchShifts() {
  const res = await apiClient.get("/shifts");
  return res.data;
}

export async function fetchShift(id) {
  const res = await apiClient.get(`/shifts/${id}`);
  return res.data;
}

export async function fetchShiftByDate(date) {
  const res = await apiClient.get(`/shifts/by-date/${date}`);
  return res.data;
}

export async function fetchCalendarData(year, month) {
  const res = await apiClient.get(`/shifts/calendar/${year}/${month}`);
  return res.data;
}

export async function createShift(data) {
  const res = await apiClient.post("/shifts", data);
  return res.data;
}

export async function updateShift(id, data) {
  const res = await apiClient.put(`/shifts/${id}`, data);
  return res.data;
}

export async function fetchTaxRate(year) {
  const res = await apiClient.get(`/tax-rate/${year}`);
  return res.data;
}

export async function saveTaxRate(year, rate) {
  const res = await apiClient.put(`/tax-rate/${year}`, { rate });
  return res.data;
}

export async function fetchWrvuRate(year) {
  const res = await apiClient.get(`/wrvu-rate/${year}`);
  return res.data;
}

export async function saveWrvuRate(year, rate, applyToPrevious = false) {
  const res = await apiClient.put(`/wrvu-rate/${year}`, { rate, apply_to_previous: applyToPrevious });
  return res.data;
}

export async function deleteShift(id) {
  const res = await apiClient.delete(`/shifts/${id}`);
  return res.data;
}

// Admin API calls (require admin privileges)
export async function fetchAdminStats() {
  const res = await apiClient.get("/admin/stats");
  return res.data;
}

export async function fetchAllUsers() {
  const res = await apiClient.get("/admin/users");
  return res.data;
}

export async function fetchPendingUsers() {
  const res = await apiClient.get("/admin/users/pending");
  return res.data;
}

export async function approveUser(userId) {
  const res = await apiClient.post(`/admin/users/${userId}/approve`);
  return res.data;
}

export async function rejectUser(userId) {
  const res = await apiClient.post(`/admin/users/${userId}/reject`);
  return res.data;
}

export async function toggleUserAdmin(userId) {
  const res = await apiClient.post(`/admin/users/${userId}/toggle-admin`);
  return res.data;
}

export async function toggleMasterAdmin(userId) {
  const res = await apiClient.post(`/admin/users/${userId}/toggle-master-admin`);
  return res.data;
}

// Messaging API calls
export async function sendMessageToAdmin(content) {
  const res = await apiClient.post("/messages", { content });
  return res.data;
}

export async function fetchMyMessages() {
  const res = await apiClient.get("/messages/my");
  return res.data;
}

export async function fetchAllMessages() {
  const res = await apiClient.get("/admin/messages");
  return res.data;
}

export async function replyToMessage(messageId, content) {
  const res = await apiClient.post(`/admin/messages/${messageId}/reply`, { content });
  return res.data;
}

export async function markMessageRead(messageId) {
  const res = await apiClient.post(`/admin/messages/${messageId}/mark-read`);
  return res.data;
}

export async function deleteMessage(messageId) {
  const res = await apiClient.delete(`/admin/messages/${messageId}`);
  return res.data;
}

// User CPT Codes (reference list)
export async function fetchUserCptCodes() {
  const res = await apiClient.get("/cpt-codes");
  return res.data;
}

export async function addUserCptCode(code, description, wrvu) {
  const res = await apiClient.post("/cpt-codes", { code, description, wrvu });
  return res.data;
}

export async function updateUserCptCode(codeId, code, description, wrvu) {
  const res = await apiClient.put(`/cpt-codes/${codeId}`, { code, description, wrvu });
  return res.data;
}

export async function deleteUserCptCode(codeId) {
  const res = await apiClient.delete(`/cpt-codes/${codeId}`);
  return res.data;
}

export async function reorderUserCptCodes(orderedIds) {
  const res = await apiClient.put("/cpt-codes/reorder", { ordered_ids: orderedIds });
  return res.data;
}

// Alias for adding to Quick-Add CPT List (uses CPT codes endpoint, not quick-add-visits)
export async function addToQuickAddCptList(code, description, wrvu) {
  const res = await apiClient.post("/cpt-codes", { code, description, wrvu });
  return res.data;
}

// Theme Preference API calls
export async function updateThemePreference(theme) {
  const res = await apiClient.put("/auth/theme-preference", { theme });
  return res.data;
}

export async function fetchThemePreference() {
  const res = await apiClient.get("/auth/theme-preference");
  return res.data;
}

// Subscription API calls
export async function fetchSubscriptionStatus() {
  const res = await apiClient.get("/subscription/status");
  return res.data;
}

export async function fetchSubscriptionPlans() {
  const res = await apiClient.get("/subscription/plans");
  return res.data;
}

export async function startFreeTrial() {
  const res = await apiClient.post("/subscription/start-trial");
  return res.data;
}

export async function createCheckoutSession(planId) {
  const originUrl = window.location.origin;
  const res = await apiClient.post("/subscription/checkout", {
    plan_id: planId,
    origin_url: originUrl
  });
  return res.data;
}

export async function createPortalSession() {
  const returnUrl = `${window.location.origin}/rvu-tracker/settings`;
  const res = await apiClient.post("/subscription/manage", { return_url: returnUrl });
  return res.data;
}

export async function checkCheckoutStatus(sessionId) {
  const res = await apiClient.get(`/subscription/checkout/status/${sessionId}`);
  return res.data;
}

// Password Reset API calls
export async function getSecurityQuestions() {
  const res = await apiClient.get("/auth/security-questions");
  return res.data;
}

export async function forgotPassword(email) {
  const res = await apiClient.post("/auth/forgot-password", { email });
  return res.data;
}

export async function resetPassword(email, securityAnswer, newPassword) {
  const res = await apiClient.post("/auth/reset-password", {
    email,
    security_answer: securityAnswer,
    new_password: newPassword
  });
  return res.data;
}

// Complimentary / Free Access (admin only)
export async function grantComplimentaryAccess(userId) {
  const res = await apiClient.post(`/admin/users/${userId}/grant-complimentary`);
  return res.data;
}

export async function revokeComplimentaryAccess(userId) {
  const res = await apiClient.post(`/admin/users/${userId}/revoke-complimentary`);
  return res.data;
}

export async function grantLegacyAccess() {
  const res = await apiClient.post("/admin/grant-legacy-access");
  return res.data;
}

// Backup API calls (admin only)
export async function fetchBackups() {
  const res = await apiClient.get("/admin/backups");
  return res.data;
}

export async function createBackup() {
  const res = await apiClient.post("/admin/backup");
  return res.data;
}

export async function deleteBackup(backupName) {
  const res = await apiClient.delete(`/admin/backup/${backupName}`);
  return res.data;
}

export async function restoreBackup(backupName) {
  const res = await apiClient.post(`/admin/backup/restore/${backupName}`);
  return res.data;
}

// Webhook status (admin only)
export async function fetchWebhookStatus() {
  const res = await apiClient.get("/admin/webhook-status");
  return res.data;
}

